import {
  componentKey,
  eligibleTechniques,
  textValue
} from '@/entities/component/component.model'

export const defaultTechniques = ['GVI', 'RVI', 'CVI', 'FVI']

export function buildDraftCatalog (companyId, sites, assets, components) {
  if (![sites, assets, components].every(Array.isArray))
    throw new Error('Site, asset and component APIs must return arrays.')
  const siteNames = new Map(
    sites.map(row => [textValue(row.site_id), textValue(row.WindFarm)])
  )
  const assetNames = new Map(
    assets.map(row => [
      JSON.stringify([textValue(row.site_id), textValue(row.asset_id)]),
      textValue(row.Asset)
    ])
  )
  const rows = []
  let excluded = 0
  const keys = new Set()
  components.forEach(raw => {
    const siteId = textValue(raw.site_id)
    const assetId = textValue(raw.asset_id)
    const code = textValue(raw.rds_code)
    const assetKey = JSON.stringify([siteId, assetId])
    if (
      !siteId ||
      !assetId ||
      !code ||
      !siteNames.has(siteId) ||
      !assetNames.has(assetKey) ||
      (raw.company_id != null &&
        textValue(raw.company_id) !== textValue(companyId))
    ) {
      excluded += 1
      return
    }
    const key = componentKey(raw)
    if (keys.has(key))
      throw new Error(
        'The component catalogue has duplicate identities. Resolve duplicates before drafting a scope.'
      )
    keys.add(key)
    rows.push({
      key,
      sourceId: raw.id ?? null,
      siteId,
      assetId,
      code,
      name: textValue(raw.description) || code,
      subsystem: textValue(raw.subsystem) || 'Not recorded',
      siteLabel: siteNames.get(siteId) || siteId,
      assetLabel: assetNames.get(assetKey) || assetId,
      eligible: eligibleTechniques(raw)
    })
  })
  return { rows, excluded }
}

export function emptyDraft () {
  return {
    name: '',
    owner: '',
    due: '',
    frequency: '12',
    notes: '',
    startingTechnique: 'RVI',
    componentIds: [],
    assignments: {},
    techniques: [...defaultTechniques]
  }
}

export function toggleDraftScope (draft, ids) {
  const selected = new Set(draft.componentIds)
  const remove = ids.every(id => selected.has(id))
  const assignments = { ...draft.assignments }
  ids.forEach(id => {
    if (remove) selected.delete(id)
    else {
      selected.add(id)
      if (!Object.hasOwn(assignments, id))
        assignments[id] = [draft.startingTechnique]
    }
  })
  return { ...draft, componentIds: [...selected], assignments }
}

export function assignDraftTechnique (draft, ids, technique, enabled) {
  const assignments = { ...draft.assignments }
  const selected = new Set(draft.componentIds)
  ids
    .filter(id => selected.has(id))
    .forEach(id => {
      const values = new Set(assignments[id] || [])
      if (enabled) values.add(technique)
      else values.delete(technique)
      assignments[id] = [...values]
    })
  return { ...draft, assignments }
}

export function draftRows (draft, catalog) {
  const ids = new Set(draft.componentIds)
  return catalog.filter(row => ids.has(row.key))
}

export function validateDraft (draft, catalog, throughStep = 2) {
  if (!draft.name.trim() || !draft.owner.trim())
    throw new Error('Enter a schedule name and accountable owner.')
  if (draft.name.trim().length > 120 || draft.owner.trim().length > 80)
    throw new Error('The name or owner is too long.')
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.due))
    throw new Error('Enter a valid first due date.')
  const date = new Date(draft.due + 'T00:00:00Z')
  if (
    !Number.isFinite(date.getTime()) ||
    date.toISOString().slice(0, 10) !== draft.due
  )
    throw new Error('Enter a valid first due date.')
  const frequency = Number(draft.frequency)
  if (!Number.isInteger(frequency) || frequency < 1 || frequency > 120)
    throw new Error(
      'Repeat interval must be a whole number from 1 to 120 months.'
    )
  if (throughStep < 1) return
  const selected = draftRows(draft, catalog)
  if (!selected.length) throw new Error('Select at least one component.')
  if (selected.length !== draft.componentIds.length)
    throw new Error(
      'Some selected components are no longer available. Remove unavailable selections before continuing.'
    )
  if (throughStep < 2) return
  if (selected.some(row => !(draft.assignments[row.key] || []).length))
    throw new Error(
      'Assign at least one technique to every selected component.'
    )
}

export function makeDraftSnapshot (draft, catalog, companyId, id) {
  validateDraft(draft, catalog)
  const selected = draftRows(draft, catalog)
  return {
    schema: 'mfs-local-draft/v1',
    id,
    companyId: textValue(companyId),
    savedAt: new Date().toISOString(),
    details: {
      name: draft.name.trim(),
      owner: draft.owner.trim(),
      due: draft.due,
      frequency: Number(draft.frequency),
      notes: draft.notes.trim()
    },
    draft: structuredClone(draft),
    components: selected.map(row => ({
      ...row,
      techniques: [...draft.assignments[row.key]]
    }))
  }
}

export function draftInScope (snapshot, siteId, assetId) {
  return snapshot.components.some(
    row =>
      (!siteId || row.siteId === textValue(siteId)) &&
      (!assetId || row.assetId === textValue(assetId))
  )
}

export function downloadDraft (snapshot) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' })
  )
  const link = document.createElement('a')
  link.href = url
  link.download = snapshot.id + '.json'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
